import React, { memo, useCallback, useState } from 'react'
import AllowDuplicates from '../../data/import/AllowDuplicates'
import { Button } from '@material-ui/core'
import Checkbox from '@material-ui/core/Checkbox'
import clsx from 'clsx'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import FormControl from '@material-ui/core/FormControl'
import FormGroup from '@material-ui/core/FormGroup'
import FormLabel from '@material-ui/core/FormLabel'
import Grid from '@material-ui/core/Grid'
import InfoItem from '../../components/InfoDialog'
import LinearProgress from '@material-ui/core/LinearProgress'
import { makeStyles } from '@material-ui/core/styles'
import Radio from '@material-ui/core/Radio'
import RadioGroup from '@material-ui/core/RadioGroup'
import SettingsPanel from '../../components/settings/SettingsPanel'
import TextField from '@material-ui/core/TextField'
import Clear from '../../data/import/Clear'
import ResolveImportFile from '../../data/import/ImportResolver'
import LOG from '../../logging/Logger'

const infoMessage = {
  allow: {
    title: 'Allow Duplicates',
    subTitle: 'Just Import',
    body: `Adds a new item for each item in the import file. Nothing is overwritten
        and no attempt is made to de-duplicate. If parent items (such as
        projects) are not imported then todos being imported using those items
        will, instead, not be allocated to a parent.`
  },
  clear: {
    title: 'Clear',
    subTitle: 'A clean slate',
    body: `Removes all existing items and imports everything from the specified
        file. Be careful, this will delete all existing projects, areas, repeats, tags and todos.`
  },
  overwrite: {
    title: 'Overwrite (coming soon)',
    subTitle: 'Keep Incoming',
    body: `Any conflicts will be resolved by keeping the data from the file you are
        importing.`
  },
  merge: {
    title: 'Merge (coming soon)',
    subTitle: 'Smart Merging',
    body: `Will keep all missing items from both sides and attempt to itelligently
        merge any items that appear on both sides.`
  },
  missing: {
    title: 'Only Missing (coming soon)',
    subTitle: 'Only Missing',
    body: `Adds a new items for each item in the import file. Nothing is
        overwritten and no attempt is made to de-duplicate.`
  },
  importTypes: [
    {
      title: 'Import Types',
      subTitle: 'Native',
      body: `This is the default format exported by Getting Focused and it the most
        reliable for importing / exporting.`
    },
    {
      subTitle: 'Get It Done',
      body: `It is possible to import the current export format from the Get It Done 5.0 app.
      Not all features are supported due to the nature of the export file.`
    }
  ]
}

const useStyles = makeStyles(theme => ({
  margin: {
    margin: theme.spacing(1)
  },
  input: {
    display: 'none'
  },
  formHint: {
    fontSize: '0.6rem',
    lineHeight: 1.4,
    margin: '-5px auto 5px',
    color: '#999',

    error: {
      color: '#c00',
      fontSize: '0.8rem'
    }
  }
}))

const closedInfo = {
  allow: false,
  clear: false,
  overwrite: false,
  merge: false,
  missing: false,
  importTypes: false
}

const SettingsImport = memo(
  ({
    createFullTodo,
    createProject,
    createArea,
    createRepeat,
    createTag,
    deleteAll,
    setInfo,
    setError
  }) => {
    LOG.debug('Render: SettingsImport')

    const classes = useStyles()

    const [importEnabled, setImportEnabled] = useState(false)
    const [importStrategy, setImportStrategy] = useState('allow')
    const [fileName, setFileName] = useState('')
    const [progress, setProgress] = useState(0)

    const [importOptions, setImportOptions] = useState({
      areas: true,
      projects: true,
      repeats: true,
      tags: true,
      todos: true
    })

    const [importCount, setImportCount] = useState({
      areasCount: 0,
      projectsCount: 0,
      repeatsCount: 0,
      tagsCount: 0,
      todosCount: 0
    })

    const [importData, setImportData] = useState(null)
    const [loading, setLoading] = useState(false)

    const isValidImport = useCallback(
      content => {
        const importJson = ResolveImportFile(content)
        if (importJson) {
          setImportCount({
            ...importCount,
            areasCount: importJson.areas.length,
            projectsCount: importJson.projects.length,
            repeatsCount: importJson.repeats.length,
            tagsCount: importJson.tags.length,
            todosCount: importJson.todos.length
          })
          setImportData(importJson)
          return true
        }

        return false
      },
      [importCount]
    )

    const incrementProgress = useCallback(increment => {
      setProgress(oldProgress => oldProgress + increment)
    }, [])

    const importCompleted = useCallback(() => {
      setLoading(false)
      setInfo('Import Complete')
    }, [setInfo])

    const handleImport = useCallback(() => {
      setLoading(true)
      let importer
      switch (importStrategy) {
        case 'allow':
          importer = new AllowDuplicates(
            importData,
            importOptions,
            createArea,
            createProject,
            createRepeat,
            createTag,
            createFullTodo
          )
          break
        case 'clear':
          importer = new Clear(
            importData,
            importOptions,
            createArea,
            createProject,
            createRepeat,
            createTag,
            createFullTodo,
            deleteAll
          )
          break
        default:
          importer = null
      }
      if (importer) {
        setProgress(0)
        importer.import(incrementProgress, importCompleted)
      } else {
        setLoading(false)
      }
    }, [
      createArea,
      createFullTodo,
      createProject,
      createRepeat,
      createTag,
      deleteAll,
      importCompleted,
      importData,
      importOptions,
      importStrategy,
      incrementProgress
    ])

    const onChange = useCallback(
      e => {
        setImportEnabled(false)
        setFileName('')
        if (e.target.files.length) {
          const fl = e.target.files[0]
          setFileName(fl.name)
          const fileReader = new FileReader()
          fileReader.onloadend = () => {
            const content = fileReader.result
            if (isValidImport(content)) {
              setImportEnabled(true)
            } else {
              setImportEnabled(false)
              setError('Incorrect file format')
              setImportData(null)
            }
          }
          fileReader.readAsText(fl)
          LOG.debug(fl)
        }
      },
      [isValidImport, setError]
    )

    const handleStrategyChange = useCallback(event => {
      setImportStrategy(event.target.value)
    }, [])

    const handleImportsSelected = useCallback(
      event => {
        setImportOptions({
          ...importOptions,
          [event.target.name]: event.target.checked
        })
      },
      [importOptions]
    )

    const { areas, projects, repeats, tags, todos } = importOptions
    const {
      areasCount,
      projectsCount,
      repeatsCount,
      tagsCount,
      todosCount
    } = importCount

    const [showInfo, setShowInfo] = useState(closedInfo)
    const handleInfoClose = useCallback(() => {
      setShowInfo(closedInfo)
    }, [])

    const handleInfoClick = useCallback(
      item => {
        setShowInfo({ ...showInfo, [item]: true })
      },
      [showInfo]
    )

    return (
      <div>
        <SettingsPanel title='Import'>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <span className={clsx('d-block', classes.formHint)}>
                Select a file to import and the import strategy you want to use
                then hit import
              </span>
            </Grid>
          </Grid>
          <FormControl component='fieldset'>
            <Grid container spacing={3}>
              <Grid item xs={9}>
                <input
                  accept='.json, .csv'
                  className={classes.input}
                  id='contained-button-file'
                  multiple
                  type='file'
                  onChange={onChange}
                  data-lpignore={true}
                />
                <label htmlFor='contained-button-file'>
                  <Button
                    disabled={loading}
                    variant='contained'
                    color='primary'
                    component='span'
                  >
                    Select file
                  </Button>
                </label>
              </Grid>
              <Grid item xs={3}>
                <InfoItem
                  open={showInfo.importTypes}
                  onClose={handleInfoClose}
                  onClick={() => handleInfoClick('importTypes')}
                  content={infoMessage.importTypes}
                />
              </Grid>
            </Grid>
          </FormControl>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                id='standard-full-width'
                label='File to import'
                disabled
                style={{ margin: 8 }}
                placeholder='Please choose a file to import'
                fullWidth
                margin='normal'
                InputLabelProps={{
                  shrink: true
                }}
                value={fileName}
                inputProps={{ 'data-lpignore': 'true' }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl
                component='fieldset'
                className={clsx(classes.margin)}
              >
                <FormLabel component='legend'>Import Strategy</FormLabel>
                <RadioGroup
                  aria-label='strategy'
                  name='strategy'
                  value={importStrategy}
                  onChange={handleStrategyChange}
                >
                  <Grid container spacing={3}>
                    <Grid item xs={9}>
                      <FormControlLabel
                        value='allow'
                        control={<Radio disabled={loading} />}
                        label={infoMessage.allow.title}
                      />
                    </Grid>
                    <Grid item xs={1}>
                      <InfoItem
                        open={showInfo.allow}
                        onClose={handleInfoClose}
                        onClick={() => handleInfoClick('allow')}
                        content={infoMessage.allow}
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={3}>
                    <Grid item xs={9}>
                      <FormControlLabel
                        value='clear'
                        control={<Radio disabled={loading} />}
                        label={infoMessage.clear.title}
                      />
                    </Grid>
                    <Grid item xs={1}>
                      <InfoItem
                        open={showInfo.clear}
                        onClose={handleInfoClose}
                        onClick={() => handleInfoClick('clear')}
                        content={infoMessage.clear}
                      />
                    </Grid>
                  </Grid>
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl
                component='fieldset'
                className={clsx(classes.margin)}
              >
                <FormLabel component='legend'>Items to import</FormLabel>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        disabled={loading}
                        checked={areas}
                        onChange={handleImportsSelected}
                        name='areas'
                      />
                    }
                    label={'Areas (' + areasCount + ')'}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        disabled={loading}
                        checked={projects}
                        onChange={handleImportsSelected}
                        name='projects'
                      />
                    }
                    label={'Projects (' + projectsCount + ')'}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        disabled={loading}
                        checked={repeats}
                        onChange={handleImportsSelected}
                        name='repeats'
                      />
                    }
                    label={'Repeats (' + repeatsCount + ')'}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        disabled={loading}
                        checked={tags}
                        onChange={handleImportsSelected}
                        name='tags'
                      />
                    }
                    label={'Tags (' + tagsCount + ')'}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        disabled={loading}
                        checked={todos}
                        onChange={handleImportsSelected}
                        name='todos'
                      />
                    }
                    label={'Todos (' + todosCount + ')'}
                  />
                </FormGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Button
                variant='contained'
                color='primary'
                disabled={!importEnabled || loading}
                className={clsx(classes.margin)}
                onClick={handleImport}
              >
                Import
              </Button>
            </Grid>
            <Grid item xs={12}>
              {loading && (
                <LinearProgress variant='determinate' value={progress} />
              )}
            </Grid>
          </Grid>
        </SettingsPanel>
      </div>
    )
  }
)

export default SettingsImport
